
import { useState } from 'react';

interface CreateCompanyModalProps {
  onClose: () => void;
}

const CreateCompanyModal = ({ onClose }: CreateCompanyModalProps) => {
  const [step, setStep] = useState(1);
  const [companyData, setCompanyData] = useState({
    name: '',
    domain: '',
    adminEmail: '',
    adminName: '',
    plan: 'professional',
    industry: '',
    description: '',
    logo: null as File | null,
    primaryColor: '#2563eb',
    secondaryColor: '#64748b',
    customDomain: '',
    features: {
      projects: true,
      calendar: true,
      leads: true,
      billing: false,
      analytics: true,
      customBranding: true
    }
  });

  const handleNext = () => {
    if (step < 3) setStep(step + 1);
  };

  const handlePrevious = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleSubmit = () => {
    console.log('Creating company:', companyData);
    // Here you would typically send the data to your API
    onClose();
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setCompanyData(prev => ({ ...prev, logo: file }));
    }
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <div>
        <h4 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Company Name *
            </label>
            <input
              type="text"
              value={companyData.name}
              onChange={(e) => setCompanyData(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
              placeholder="Enter company name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Subdomain *
            </label>
            <div className="flex">
              <input
                type="text"
                value={companyData.domain}
                onChange={(e) => setCompanyData(prev => ({ ...prev, domain: e.target.value }))}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-l-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                placeholder="company"
              />
              <span className="px-3 py-2 bg-gray-100 border border-l-0 border-gray-300 rounded-r-lg text-sm text-gray-600">
                .teamsday.com
              </span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Admin Name *
            </label>
            <input
              type="text"
              value={companyData.adminName}
              onChange={(e) => setCompanyData(prev => ({ ...prev, adminName: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
              placeholder="Enter admin full name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Admin Email *
            </label>
            <input
              type="email"
              value={companyData.adminEmail}
              onChange={(e) => setCompanyData(prev => ({ ...prev, adminEmail: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
              placeholder="admin@company.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Industry
            </label>
            <select
              value={companyData.industry}
              onChange={(e) => setCompanyData(prev => ({ ...prev, industry: e.target.value }))}
              className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
            >
              <option value="">Select industry</option>
              <option value="construction">Construction</option>
              <option value="technology">Technology</option>
              <option value="design">Design & Creative</option>
              <option value="consulting">Consulting</option>
              <option value="manufacturing">Manufacturing</option>
              <option value="healthcare">Healthcare</option>
              <option value="education">Education</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Plan
            </label>
            <select
              value={companyData.plan}
              onChange={(e) => setCompanyData(prev => ({ ...prev, plan: e.target.value }))}
              className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
            >
              <option value="trial">Trial (14 days)</option>
              <option value="basic">Basic ($29/month)</option>
              <option value="professional">Professional ($79/month)</option>
              <option value="enterprise">Enterprise ($199/month)</option>
            </select>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={companyData.description}
              onChange={(e) => setCompanyData(prev => ({ ...prev, description: e.target.value }))}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
              placeholder="Brief description of the company..."
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div>
        <h4 className="text-lg font-medium text-gray-900 mb-4">Branding & Customization</h4>
        
        {/* Logo Upload */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Company Logo
          </label>
          <div className="flex items-center space-x-4">
            <div className="w-20 h-20 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
              {companyData.logo ? (
                <img
                  src={URL.createObjectURL(companyData.logo)}
                  alt="Logo preview"
                  className="w-full h-full object-cover rounded-lg"
                />
              ) : (
                <i className="ri-image-line text-gray-400 text-2xl"></i>
              )}
            </div>
            <div>
              <input
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                className="hidden"
                id="logo-upload"
              />
              <label
                htmlFor="logo-upload"
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 cursor-pointer text-sm font-medium whitespace-nowrap"
              >
                Upload Logo
              </label>
              <p className="text-xs text-gray-500 mt-1">PNG, JPG up to 2MB</p>
            </div>
          </div>
        </div>

        {/* Color Customization */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Primary Color
            </label>
            <div className="flex items-center space-x-3">
              <input
                type="color"
                value={companyData.primaryColor}
                onChange={(e) => setCompanyData(prev => ({ ...prev, primaryColor: e.target.value }))}
                className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
              />
              <input
                type="text"
                value={companyData.primaryColor}
                onChange={(e) => setCompanyData(prev => ({ ...prev, primaryColor: e.target.value }))}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Secondary Color
            </label>
            <div className="flex items-center space-x-3">
              <input
                type="color"
                value={companyData.secondaryColor}
                onChange={(e) => setCompanyData(prev => ({ ...prev, secondaryColor: e.target.value }))}
                className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
              />
              <input
                type="text"
                value={companyData.secondaryColor}
                onChange={(e) => setCompanyData(prev => ({ ...prev, secondaryColor: e.target.value }))}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
              />
            </div>
          </div>
        </div>

        {/* Preview */}
        <div className="mt-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Preview
          </label>
          <div className="border border-gray-200 rounded-lg p-4">
            <div 
              className="h-24 rounded-lg mb-3 flex items-center justify-center"
              style={{ backgroundColor: companyData.primaryColor }}
            >
              <div className="text-white font-bold text-lg">
                {companyData.name || 'Company Name'}
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div 
                className="w-8 h-8 rounded"
                style={{ backgroundColor: companyData.primaryColor }}
              ></div>
              <div 
                className="w-8 h-8 rounded"
                style={{ backgroundColor: companyData.secondaryColor }}
              ></div>
              <span className="text-sm text-gray-600">Color Palette</span>
            </div>
          </div>
        </div>

        {/* Custom Domain */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Custom Domain (Optional)
          </label>
          <input
            type="text"
            value={companyData.customDomain}
            onChange={(e) => setCompanyData(prev => ({ ...prev, customDomain: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
            placeholder="app.yourcompany.com"
          />
          <p className="text-xs text-gray-500 mt-1">You can configure a custom domain later</p>
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div>
        <h4 className="text-lg font-medium text-gray-900 mb-4">Features & Permissions</h4>
        
        <div className="space-y-4">
          {Object.entries(companyData.features).map(([feature, enabled]) => (
            <div key={feature} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
              <div>
                <h5 className="text-sm font-medium text-gray-900 capitalize">
                  {feature.replace(/([A-Z])/g, ' $1').trim()}
                </h5>
                <p className="text-sm text-gray-500">
                  {feature === 'projects' && 'Project management and tracking'}
                  {feature === 'calendar' && 'Calendar and scheduling features'}
                  {feature === 'leads' && 'Lead management and CRM'}
                  {feature === 'billing' && 'Billing and invoicing system'}
                  {feature === 'analytics' && 'Analytics and reporting'}
                  {feature === 'customBranding' && 'Custom branding and whitelabeling'}
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={enabled}
                  onChange={(e) => setCompanyData(prev => ({
                    ...prev,
                    features: { ...prev.features, [feature]: e.target.checked }
                  }))}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
              </label>
            </div>
          ))}
        </div>

        {/* Summary */}
        <div className="mt-8 p-4 bg-gray-50 rounded-lg">
          <h5 className="text-sm font-medium text-gray-900 mb-3">Setup Summary</h5>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Company:</span>
              <span className="text-gray-900">{companyData.name || 'Not specified'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Domain:</span>
              <span className="text-gray-900">{companyData.domain || 'Not specified'}.teamsday.com</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Admin:</span>
              <span className="text-gray-900">{companyData.adminEmail || 'Not specified'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Plan:</span>
              <span className="text-gray-900 capitalize">{companyData.plan}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Features:</span>
              <span className="text-gray-900">
                {Object.values(companyData.features).filter(Boolean).length} enabled
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Create New Company</h3>
            <p className="text-sm text-gray-600">Step {step} of 3</p>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded"
          >
            <i className="ri-close-line text-gray-500"></i>
          </button>
        </div>

        {/* Progress Bar */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center">
            {[1, 2, 3].map((stepNumber) => (
              <div key={stepNumber} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  stepNumber <= step ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-600'
                }`}>
                  {stepNumber}
                </div>
                {stepNumber < 3 && (
                  <div className={`w-16 h-1 mx-2 ${
                    stepNumber < step ? 'bg-red-600' : 'bg-gray-200'
                  }`}></div>
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-2 text-sm">
            <span className={step >= 1 ? 'text-red-600' : 'text-gray-500'}>Basic Info</span>
            <span className={step >= 2 ? 'text-red-600' : 'text-gray-500'}>Branding</span>
            <span className={step >= 3 ? 'text-red-600' : 'text-gray-500'}>Features</span>
          </div>
        </div>

        <div className="p-6">
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
        </div>

        <div className="flex items-center justify-between p-6 border-t border-gray-200">
          <button
            onClick={handlePrevious}
            disabled={step === 1}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            <i className="ri-arrow-left-line mr-2"></i>
            Previous
          </button>
          
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 whitespace-nowrap"
            >
              Cancel
            </button>
            {step < 3 ? (
              <button
                onClick={handleNext}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 whitespace-nowrap"
              >
                Next
                <i className="ri-arrow-right-line ml-2"></i>
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 whitespace-nowrap"
              >
                <i className="ri-check-line mr-2"></i>
                Create Company
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateCompanyModal;
